import {
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Check as CheckIcon, Search as SearchIcon } from '@design-system/icons';
import { TextInput } from '@design-system/ui';
import { cn } from '@foundation/utils/cn';
import { ErrorMessage } from '@shared/ui/components/inputs/ErrorMessage';

import { AdvancedSelectTrigger } from './AdvancedSelectTrigger';
import { AdvancedSelectOption } from './types';
import { Modal } from '../../Modal';

type OptionRendererProps<T> = {
  option: AdvancedSelectOption<T>;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: (value: T) => void;
  onFocus: () => void;
  ref: (el: HTMLButtonElement | null) => void;
  'data-testid'?: string;
};

export interface AdvancedSelectProps<T = string>
  extends Omit<
    SelectHTMLAttributes<HTMLDivElement>,
    'onChange' | 'value' | 'className'
  > {
  options: AdvancedSelectOption<T>[];
  value?: T;
  onChange: (e: { target: { name?: string; value: T } }) => void;
  error?: string;
  label?: string;
  allowSearch?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  modalTitle?: string;
  styles?: {
    wrapper?: string;
    modal?: string;
    trigger?: string;
  };
  renderTrigger?: (
    selectedOption: AdvancedSelectOption<T> | undefined,
  ) => ReactNode;
  renderOption?: (props: OptionRendererProps<T>) => ReactNode;
  renderFooter?: () => ReactNode;
  getOptionKey?: (option: AdvancedSelectOption<T>) => string;
  'data-testid'?: string;
}

const defaultRenderOption = <T,>({
  option,
  isSelected,
  isFocused,
  onSelect,
  onFocus,
  ref,
  'data-testid': dataTestId,
}: OptionRendererProps<T>) => (
  <button
    ref={ref}
    type="button"
    onMouseDown={() => onSelect(option.value)}
    onFocus={() => onFocus()}
    className={cn(
      'w-full pl-6 pr-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left focus:outline-none',
      isSelected && 'bg-alert-positive-primary',
      isFocused && 'bg-gray-200',
    )}
    data-testid={dataTestId ? dataTestId : undefined}
  >
    <span className="text-primary">{option.label}</span>
    {isSelected && (
      <span className="ml-auto text-white mr-2 bg-alert-positive-500 rounded-full p-1">
        <CheckIcon size={16} />
      </span>
    )}
  </button>
);

function AdvancedSelectInner<T = string>(
  {
    options,
    name,
    value,
    onChange,
    error,
    label,
    required,
    allowSearch = true,
    searchPlaceholder,
    noResultsText,
    disabled = false,
    modalTitle,
    styles = {
      wrapper: 'w-full',
      modal: 'h-[90vh] md:max-w-xl',
      trigger: '',
    },
    renderTrigger,
    renderOption,
    renderFooter,
    getOptionKey,
    'data-testid': dataTestId,
    ...props
  }: AdvancedSelectProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const typeaheadQuery = useRef('');
  const typeaheadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((option) => {
    const searchText = option.searchableText || option.label;
    return searchText.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // When modal opens, set selected index to the current value
  useEffect(() => {
    if (isOpen && value !== undefined && searchQuery === '') {
      const currentIndex = filteredOptions.findIndex(
        (opt) => opt.value === value,
      );
      if (currentIndex !== -1) {
        setSelectedIndex(currentIndex);
      }
    }
  }, [isOpen]);

  // Reset selected index when search query changes
  useEffect(() => {
    if (searchQuery !== '') {
      setSelectedIndex(-1);
    }
  }, [searchQuery]);

  // Scroll selected item into view and focus it
  useEffect(() => {
    if (selectedIndex >= 0 && listItemRefs.current[selectedIndex]) {
      listItemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      listItemRefs.current[selectedIndex]?.focus();
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown: EventListener = (e: Event) => {
      const keyboardEvent = e as unknown as KeyboardEvent;
      switch (keyboardEvent.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
        case ' ':
          if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
            e.preventDefault();
            handleSelect(filteredOptions[selectedIndex].value);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (typeaheadTimeoutRef.current) {
        clearTimeout(typeaheadTimeoutRef.current);
      }
    };
  }, [isOpen, selectedIndex, filteredOptions]);

  const handleSelect = useCallback(
    (optionValue: T) => {
      const event = {
        target: {
          name,
          value: optionValue,
        },
      };
      onChange(event);
      handleClose();
    },
    [name, onChange],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(-1);
  }, []);

  // Handle keyboard navigation on trigger when closed
  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled || isOpen) return;

      // Handle arrow keys, Enter, and Space to open modal
      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowUp' ||
        e.key === 'Enter' ||
        e.key === ' '
      ) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        return;
      }

      // Handle typeahead search
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();

        // Clear previous timeout
        if (typeaheadTimeoutRef.current) {
          clearTimeout(typeaheadTimeoutRef.current);
        }

        // Append the key to the query
        typeaheadQuery.current += e.key.toLowerCase();

        // Find the first matching option
        const currentQuery = typeaheadQuery.current;
        const currentIndex = options.findIndex((opt) => opt.value === value);
        const startIndex = currentIndex + 1;

        // First, try to find from current position onwards
        let matchIndex = options.findIndex((option, idx) => {
          const searchText = option.searchableText || option.label;
          return (
            idx >= startIndex &&
            searchText.toLowerCase().startsWith(currentQuery)
          );
        });

        // If not found, search from the beginning
        if (matchIndex === -1) {
          matchIndex = options.findIndex((option) => {
            const searchText = option.searchableText || option.label;
            return searchText.toLowerCase().startsWith(currentQuery);
          });
        }

        // Update value if match found
        if (matchIndex !== -1 && options[matchIndex]) {
          handleSelect(options[matchIndex].value);
        }

        // Reset the query after 800ms
        typeaheadTimeoutRef.current = setTimeout(() => {
          typeaheadQuery.current = '';
        }, 800);
      }
    },
    [isOpen, disabled, value, options, handleSelect],
  );

  const defaultGetOptionKey = (option: AdvancedSelectOption<T>) =>
    String(option.value);

  return (
    <div
      className={styles.wrapper}
      {...props}
    >
      <input
        ref={ref}
        type="hidden"
        value={value !== undefined ? String(value) : ''}
        readOnly
      />

      <AdvancedSelectTrigger<T>
        ref={triggerButtonRef}
        disabled={disabled}
        hasError={!!error}
        className={styles.trigger}
        label={label}
        value={value}
        required={required}
        selectedOption={selectedOption}
        renderTrigger={renderTrigger}
        onOpen={() => setIsOpen(true)}
        onKeyDown={handleTriggerKeyDown}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
      />

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle}
        styles={{ modal: styles.modal }}
        initialFocus={() => listItemRefs.current[0] || undefined}
        returnFocus={() => triggerButtonRef.current || false}
      >
        <div className="flex flex-col h-full">
          {allowSearch && (
            <div className="bg-white p-4 pt-0 border-b border-gray-200 shrink-0 sticky top-0 z-10">
              <div className="relative">
                <div className="pt-1">
                  <TextInput
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startAdornment={<SearchIcon size={20} />}
                    onClear={() => setSearchQuery('')}
                    clearable
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {filteredOptions.map((option, index) => {
                  const isSelected = value === option.value;
                  const isFocused = selectedIndex === index;
                  const key = getOptionKey
                    ? getOptionKey(option)
                    : defaultGetOptionKey(option);

                  const optionRenderer = renderOption
                    ? renderOption
                    : defaultRenderOption;

                  return (
                    <li key={key}>
                      {optionRenderer({
                        option,
                        isSelected,
                        onSelect: handleSelect,
                        isFocused,
                        onFocus: () => setSelectedIndex(index),
                        ref: (el) => {
                          listItemRefs.current[index] = el;
                        },
                        'data-testid': dataTestId
                          ? `${dataTestId}-option-${option.value}`
                          : undefined,
                      })}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <>
                {noResultsText && (
                  <div className="text-center py-8 text-tertiary">
                    {noResultsText}
                  </div>
                )}
              </>
            )}
            {renderFooter && (
              <div
                className={cn({
                  'border-t border-gray-200': filteredOptions.length > 0,
                })}
              >
                {renderFooter()}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {error && <ErrorMessage message={error} />}
    </div>
  );
}

export const AdvancedSelect = forwardRef(AdvancedSelectInner) as <T = string>(
  props: AdvancedSelectProps<T> & {
    ref?: ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof AdvancedSelectInner>;
